import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BetSlipProvider } from './contexts/BetSlipContext';
import LoadingSpinner from './components/LoadingSpinner';
import BetSlip from './components/BetSlip';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import WalletPage from './pages/WalletPage';
import AccountPage from './pages/AccountPage';
import SportPage from './pages/SportPage';
import EventPage from './pages/EventPage';
import MyBetsPage from './pages/MyBetsPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMarkets from './pages/admin/AdminMarkets';
import AdminRisk from './pages/admin/AdminRisk';
import AdminHouseRisk from './pages/admin/AdminHouseRisk';
import AdminLinkedAccounts from './pages/admin/AdminLinkedAccounts';
import AdminUsers from './pages/admin/AdminUsers';
import AdminReports from './pages/admin/AdminReports';
import AdminPartners from './pages/admin/AdminPartners';
import AdminSupport from './pages/admin/AdminSupport';
import KYCPage from './pages/KYCPage';
import PartnerPage from './pages/PartnerPage';
import SupportPage from './pages/SupportPage';
import ResponsibleGamblingPage from './pages/legal/ResponsibleGamblingPage';
import TermsPage from './pages/legal/TermsPage';
import BettingRulesPage from './pages/legal/BettingRulesPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import ComplaintsPage from './pages/legal/ComplaintsPage';
import AboutPage from './pages/legal/AboutPage';

/** Redirect to /login if the user is not authenticated. */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-2">
        <LoadingSpinner height="h-8" className="w-48" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ next: location.pathname }} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BetSlipProvider>
          {/* Global verify-email reminder — shown to unverified logged-in users */}
          <VerifyEmailBanner />
          {/* Global floating BetSlip — rendered outside Routes so it persists across navigation */}
          <BetSlip />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route
              path="/verify-email"
              element={
                <PrivateRoute>
                  <VerifyEmailPage />
                </PrivateRoute>
              }
            />
            <Route path="/sport/:slug" element={<SportPage />} />
            <Route path="/event/:id" element={<EventPage />} />

            {/* Public legal / trust pages */}
            <Route path="/responsible-gambling" element={<ResponsibleGamblingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/betting-rules" element={<BettingRulesPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/complaints" element={<ComplaintsPage />} />
            <Route path="/about" element={<AboutPage />} />

            {/* Protected routes */}
            <Route
              path="/wallet"
              element={
                <PrivateRoute>
                  <WalletPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/account"
              element={
                <PrivateRoute>
                  <AccountPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/bets"
              element={
                <PrivateRoute>
                  <MyBetsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/kyc"
              element={
                <PrivateRoute>
                  <KYCPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/support"
              element={
                <PrivateRoute>
                  <SupportPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/partner"
              element={
                <PrivateRoute>
                  <PartnerPage />
                </PrivateRoute>
              }
            />
            {/* Admin tree is NOT wrapped in PrivateRoute: admin operators
                authenticate with an admin EMAIL + TOKEN (provisioned via
                my-investments-admin), not a punter login. AdminLayout's own
                EmailPrompt gates the UI and the backend enforces the token on
                every /admin API call (enforce_admin_token). Wrapping this in
                PrivateRoute (a punter-session guard) bounced operators arriving
                from the admin console to /login — they have no punter account.
                See hotfix-1-7. */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="markets" element={<AdminMarkets />} />
              <Route path="risk" element={<AdminRisk />} />
              <Route path="house" element={<AdminHouseRisk />} />
              <Route path="linked-accounts" element={<AdminLinkedAccounts />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="support" element={<AdminSupport />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BetSlipProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
